import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Observable } from 'rxjs';
import { MpsColorTheme, MpsTheme, MpsThemeService } from '../../services/mps-theme.service';

@Component({
  selector: 'mps-theme-switcher',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, MatMenuModule, MatTooltipModule, MatDividerModule],
  template: `
    <div class="mps-theme-switcher">
      <!-- Bouton principal -->
      <button mat-icon-button [matMenuTriggerFor]="themeMenu" matTooltip="Changer le thème" class="theme-toggle-btn">
        <mat-icon>{{ getThemeIcon() }}</mat-icon>
      </button>

      <!-- Menu des thèmes -->
      <mat-menu #themeMenu="matMenu" class="mps-theme-menu">
        <!-- Thèmes principaux -->
        <div class="menu-section">
          <div class="menu-title">Mode d'affichage</div>

          <button mat-menu-item (click)="setTheme('light')" [class.active]="(currentTheme$ | async) === 'light'">
            <mat-icon>light_mode</mat-icon>
            <span>Clair</span>
          </button>

          <button mat-menu-item (click)="setTheme('dark')" [class.active]="(currentTheme$ | async) === 'dark'">
            <mat-icon>dark_mode</mat-icon>
            <span>Sombre</span>
          </button>

          <button mat-menu-item (click)="setTheme('auto')" [class.active]="(currentTheme$ | async) === 'auto'">
            <mat-icon>brightness_auto</mat-icon>
            <span>Automatique</span>
          </button>
        </div>

        <mat-divider></mat-divider>

        <!-- Thèmes de couleur -->
        <div class="menu-section">
          <div class="menu-title">Couleur</div>

          <div class="color-themes">
            <button
              mat-icon-button
              (click)="setColorTheme('blue')"
              [class.active]="(currentColorTheme$ | async) === 'blue'"
              class="color-btn blue"
            >
              <div class="color-preview"></div>
            </button>

            <button
              mat-icon-button
              (click)="setColorTheme('green')"
              [class.active]="(currentColorTheme$ | async) === 'green'"
              class="color-btn green"
            >
              <div class="color-preview"></div>
            </button>

            <button
              mat-icon-button
              (click)="setColorTheme('purple')"
              [class.active]="(currentColorTheme$ | async) === 'purple'"
              class="color-btn purple"
            >
              <div class="color-preview"></div>
            </button>

            <button
              mat-icon-button
              (click)="setColorTheme('red')"
              [class.active]="(currentColorTheme$ | async) === 'red'"
              class="color-btn red"
            >
              <div class="color-preview"></div>
            </button>
          </div>
        </div>
      </mat-menu>
    </div>
  `,
  styles: [
    `
      .mps-theme-switcher {
        display: inline-block;
      }

      .theme-toggle-btn {
        transition: all 0.3s ease;

        &:hover {
          transform: scale(1.1);
        }
      }

      ::ng-deep .mps-theme-menu {
        .mat-mdc-menu-content {
          padding: 8px 0;
        }
      }

      .menu-section {
        padding: 8px 0;
      }

      .menu-title {
        padding: 8px 16px;
        font-size: 12px;
        font-weight: 600;
        text-transform: uppercase;
        color: var(--mps-text-secondary, #757575);
        letter-spacing: 0.5px;
      }

      .mat-mdc-menu-item {
        min-height: 40px;

        &.active {
          background-color: var(--mps-primary-color, #1976d2);
          color: white;

          mat-icon {
            color: white;
          }
        }

        mat-icon {
          margin-right: 8px;
        }
      }

      .color-themes {
        display: flex;
        justify-content: center;
        gap: 8px;
        padding: 8px 16px;
      }

      .color-btn {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        border: 2px solid transparent;
        transition: all 0.3s ease;

        &.active {
          border-color: var(--mps-text-primary, #212121);
          transform: scale(1.1);
        }

        &:hover {
          transform: scale(1.05);
        }

        .color-preview {
          width: 100%;
          height: 100%;
          border-radius: 50%;
        }

        &.blue .color-preview {
          background: #2563eb;
        }

        &.green .color-preview {
          background: #16a34a;
        }

        &.purple .color-preview {
          background: #9333ea;
        }

        &.red .color-preview {
          background: #dc2626;
        }
      }
    `,
  ],
})
export class MpsThemeSwitcherComponent implements OnInit {
  currentTheme$: Observable<MpsTheme>;
  currentColorTheme$: Observable<MpsColorTheme>;

  constructor() {
    this.currentTheme$ = this.themeService.theme$;
    this.currentColorTheme$ = this.themeService.colorTheme$;
  }
  themeService = inject(MpsThemeService);

  ngOnInit(): void {
    // Composant initialisé
  }

  setTheme(theme: MpsTheme): void {
    this.themeService.setTheme(theme);
  }

  setColorTheme(colorTheme: MpsColorTheme): void {
    this.themeService.setColorTheme(colorTheme);
  }

  getThemeIcon(): string {
    const currentTheme = this.themeService.getCurrentTheme();
    switch (currentTheme) {
      case 'light':
        return 'light_mode';
      case 'dark':
        return 'dark_mode';
      case 'auto':
        return 'brightness_auto';
      default:
        return 'palette';
    }
  }
}
